import { ref, computed } from 'vue'
import { isIOS, isStandalone } from './useIosInstallGuide'

// Глобальний стан BeforeInstallPrompt (Android/Desktop Chrome).
// Подія може прилетіти до монтування будь-якого компонента,
// тому слухач реєструємо одразу при імпорті модуля.

const deferredPrompt = ref(null)
const installed = ref(false)
let listenersAttached = false

const handleBeforeInstall = (e) => {
  e.preventDefault()
  deferredPrompt.value = e
}
const handleAppInstalled = () => {
  deferredPrompt.value = null
  installed.value = true
}

const attachListeners = () => {
  if (listenersAttached || typeof window === 'undefined') return
  window.addEventListener('beforeinstallprompt', handleBeforeInstall)
  window.addEventListener('appinstalled', handleAppInstalled)
  listenersAttached = true
}
attachListeners()

// Samsung Internet собирает WebAPK своим сервисом минтинга, и APK получается
// со старым targetSdkVersion — Play Protect режет установку («Небезпечний
// додаток заблоковано / створено для старішої версії ОС Android»), после чего
// прилетает «Не вдалося встановити веб-програму». Наш манифест ни при чём,
// лечится только установкой из Chrome. Событие beforeinstallprompt Samsung
// шлёт исправно, поэтому без этой проверки мы сами ведём юзера в битую установку.
export const isSamsungInternet = () => {
  if (typeof window === 'undefined') return false
  return /SamsungBrowser/i.test(window.navigator.userAgent)
}

// Открывает текущий адрес в Chrome через intent://.
// Если Chrome не установлен — browser_fallback_url уводит на его страницу в Play Market.
export const openInChrome = () => {
  if (typeof window === 'undefined') return
  const { hostname, pathname, search } = window.location
  // hash намеренно не переносим: лишний '#' сломал бы разбор самого intent-URL
  const fallback = encodeURIComponent('https://play.google.com/store/apps/details?id=com.android.chrome')
  window.location.href = `intent://${hostname}${pathname}${search}` +
    `#Intent;scheme=https;package=com.android.chrome;S.browser_fallback_url=${fallback};end`
}

export function usePwaInstall() {
  // Чи доступна нативна установка Android/Desktop Chrome.
  // Samsung Internet виключений свідомо — його WebAPK блокує Play Protect.
  const canInstallNative = computed(() =>
    !!deferredPrompt.value && !isStandalone() && !isSamsungInternet())

  // Samsung Internet: замість установки пропонуємо відкрити сайт у Chrome
  const canOpenInChrome = computed(() => isSamsungInternet() && !isStandalone())

  // Чи треба показувати кнопку ручного запуску iOS-гайду у довідці
  const canShowIosGuide = computed(() => isIOS() && !isStandalone())

  const triggerNativeInstall = async () => {
    if (!deferredPrompt.value) return { outcome: 'unavailable' }
    try {
      deferredPrompt.value.prompt()
      const { outcome } = await deferredPrompt.value.userChoice
      // Подію можна використати лише один раз
      deferredPrompt.value = null
      return { outcome }
    } catch (err) {
      console.error('PWA install error:', err)
      return { outcome: 'error', error: err }
    }
  }

  return {
    deferredPrompt: computed(() => deferredPrompt.value),
    installed: computed(() => installed.value),
    canInstallNative,
    canOpenInChrome,
    canShowIosGuide,
    triggerNativeInstall,
  }
}
